import { useEffect, useState } from 'react';
import { Download, ExternalLink, Lock, Loader2, Unlock } from 'lucide-react';
import { api } from '../api';
import { useAuth } from '../lib/contextoAuth';
import { guardarArchivo } from '../ui/descarga';
import Ayuda from '../ui/Ayuda';
import Domiciliacion from '../ops/Domiciliacion';

/* =====================================================================
   Lo que la cuenta manda al nodo · COBRO-CLIP B3 (Ajustes → Suscripción)
   =====================================================================
   Se monta DEBAJO de la suscripción de siempre (plan y uso), que gana.
   Aquí va lo que bajó en el último latido y no tenía pantalla: los
   módulos por cuenta con «Pedir este módulo», las líneas de pago con su
   enlace, los recibos y el método guardado (marca y últimos cuatro, solo
   para el Supremo). Todo dice CUÁNDO se supo: un cero sin fecha se lee
   como «no debes nada» y puede significar «nadie ha hablado con la nube».
   ===================================================================== */
export default function CuentaYPagos() {
  const { tieneNivel } = useAuth();
  const [d, setD] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let vivo = true;
    api.get('/escala/suscripcion')
      .then((r) => { if (vivo) setD(r); })
      .catch((e) => { if (vivo) setError(e.message); });
    return () => { vivo = false; };
  }, []);

  if (error) return <p className="text-sm text-red-700">{error}</p>;
  if (!d) {
    return (
      <p className="flex items-center gap-2 text-sm text-gray-500">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
        Leyendo lo que la nube dijo de esta cuenta…
      </p>
    );
  }

  // Defensivo a propósito: si la respuesta viene recortada (una versión
  // vieja del servidor, una prueba que finge otra ruta), la pantalla
  // enseña lo que hay y no se cae encima del plan de siempre.
  const modulos = Array.isArray(d.modulos) ? d.modulos : [];
  const lineas = Array.isArray(d.lineas_pendientes) ? d.lineas_pendientes : [];
  const recibos = Array.isArray(d.recibos) ? d.recibos : [];
  const leido = d.pagos_leidos_en
    ? new Date(d.pagos_leidos_en).toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' })
    : null;
  const esDueno = tieneNivel('DUENO');

  return (
    <div className="space-y-5">
      <section className="rounded-2xl border border-gray-200 bg-white p-4 space-y-3">
        <div>
          <h2 className="font-semibold text-gray-900">Módulos de tu cuenta</h2>
          <p className="text-xs text-gray-600">
            Lo que la nube dijo la última vez que este servidor habló con ella
            {modulos.find((m) => m.leido_en)
              ? ` (${new Date(modulos.find((m) => m.leido_en).leido_en).toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' })})`
              : ': todavía nada, así que todo está abierto'}.
          </p>
          <Ayuda id="escala.modulos_en_consulta" />
        </div>
        <ul className="divide-y divide-gray-100">
          {modulos.map((m) => (
            <li key={m.modulo} className="flex flex-wrap items-center gap-2 py-2 text-sm">
              {m.en_consulta
                ? <Lock className="h-4 w-4 text-amber-700" aria-hidden="true" />
                : <Unlock className="h-4 w-4 text-green-700" aria-hidden="true" />}
              <span className="flex-1 text-gray-900">{m.nombre}</span>
              <span className={m.en_consulta ? 'text-amber-800' : 'text-green-800'}>
                {m.en_consulta ? 'En consulta' : (m.encendido === null ? 'Abierto' : 'Encendido')}
              </span>
              {m.en_consulta && (m.pedir?.enlace
                ? (
                  <a href={m.pedir.enlace} target="_blank" rel="noreferrer"
                     className="inline-flex items-center gap-1 rounded-lg bg-[var(--color-marca)] px-3 py-1 font-semibold text-white">
                    <ExternalLink className="h-3 w-3" aria-hidden="true" />
                    Pagar y encender{m.pedir.monto ? ` · $ ${m.pedir.monto}` : ''}
                  </a>
                )
                : <span className="text-xs text-gray-600">Para pedirlo: {d.contacto}</span>)}
            </li>
          ))}
        </ul>
      </section>

      <section className="rounded-2xl border border-gray-200 bg-white p-4 space-y-3">
        <div>
          <h2 className="font-semibold text-gray-900">Pagos</h2>
          <p className="text-xs text-gray-600">
            {leido ? `Según la nube, el ${leido}.` : 'La nube todavía no ha mandado nada de pagos.'}
            {(d.meses_gratis_pendientes || 0) > 0 && ` Tienes ${d.meses_gratis_pendientes} mes(es) gratis por aplicar.`}
          </p>
          <Ayuda id="escala.pagos_son_copia" />
        </div>

        {lineas.length > 0 ? (
          <ul className="divide-y divide-gray-100">
            {lineas.map((l) => (
              <li key={l.id} className="flex flex-wrap items-center gap-2 py-2 text-sm">
                <span className="flex-1 text-gray-900">{l.concepto || l.servicio}</span>
                <span className="tabular-nums">$ {l.monto} {l.moneda}</span>
                {l.vence_en && <span className="text-xs text-gray-500">vence {l.vence_en}</span>}
                {l.enlace && (
                  <a href={l.enlace} target="_blank" rel="noreferrer"
                     className="inline-flex items-center gap-1 font-semibold text-[var(--color-marca)]">
                    <ExternalLink className="h-3 w-3" aria-hidden="true" /> Pagar
                  </a>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-gray-600">No hay líneas de pago pendientes.</p>
        )}

        <h3 className="pt-2 text-xs font-semibold uppercase text-gray-500">Recibos</h3>
        {recibos.length > 0 ? (
          <ul className="divide-y divide-gray-100">
            {recibos.map((r) => (
              <li key={r.folio} className="flex flex-wrap items-center gap-2 py-2 text-sm">
                <span className="font-mono text-gray-500">{r.folio}</span>
                <span className="flex-1 text-gray-900">{r.concepto}</span>
                <span className="tabular-nums">$ {r.total} {r.moneda}</span>
                {esDueno && (
                  <button type="button"
                          onClick={async () => {
                            guardarArchivo(await api.bajar(`/escala/suscripcion/recibos/${r.folio}.pdf`),
                                           `recibo-${r.folio}.pdf`);
                          }}
                          className="inline-flex items-center gap-1 font-semibold text-[var(--color-marca)]">
                    <Download className="h-3 w-3" aria-hidden="true" /> PDF
                  </button>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-gray-600">Sin recibos. Cada pago confirmado genera uno.</p>
        )}

        {esDueno && (
          <>
            <h3 className="pt-2 text-xs font-semibold uppercase text-gray-500">Método de pago</h3>
            <Domiciliacion />
            {d.metodo_de_pago?.marca || d.metodo_de_pago?.ultimos_cuatro ? (
              <p className="text-sm text-gray-900">
                {d.metodo_de_pago.marca || 'Tarjeta'} terminada en {d.metodo_de_pago.ultimos_cuatro || '····'}
                {d.metodo_de_pago.vigente_hasta ? ` · vigente hasta ${d.metodo_de_pago.vigente_hasta}` : ''}.
                TEGO nunca guarda el número completo.
              </p>
            ) : (
              <p className="text-sm text-gray-600">
                Sin método de pago guardado. Se guarda al pagar la primera línea con el enlace.
              </p>
            )}
          </>
        )}
      </section>
    </div>
  );
}
